import { FC, useEffect, useState } from 'react';
import { eventApi } from '../../../../core/api/rtk-query/event.api';
import { groupApi } from '../../../../core/api/rtk-query/group.api';
import { useAuth } from '../../../../hooks/useAuth';
import { Role } from '../../../../core/types/user.interface';
import { MainHeadingText, SecondaryHeadingTextForEmptyStates } from '../../../../styles/Text';
import { getGroupMaxPoints, getGroupUserPoints } from '../../../../utils/cards.utils';
import { getDayjsInGMT } from '../../../../utils/dayjs.utils';
import { GroupsGrid } from './grid/GroupsGrid';
import { GroupCardProps } from './grid/card/types';
import { TabInfo, Tabs } from '../tabs/Tabs';
import { StudentGroupsWrapper } from '../../../student/groups/Styles';
import { GroupsPageContainerProps, GroupsPageFromUserTypeProps } from './types';

import { getInfo } from '../../../../utils/cards.utils';

const groupTabs: TabInfo[] = [{ name: 'Активные курсы' }, { name: 'Пройденные курсы' }];

const GroupsPageContainer: FC<GroupsPageContainerProps> = ({ isLoading, groups, activeTab }) => {
  if (isLoading) {
    return <SecondaryHeadingTextForEmptyStates>ЗАГРУЗКА</SecondaryHeadingTextForEmptyStates>;
  }

  if (!groups?.length) {
    return (
      <SecondaryHeadingTextForEmptyStates>
        {activeTab === 0 ? 'Вы пока не записаны на курсы' : 'У вас пока нет завершенных курсов'}
      </SecondaryHeadingTextForEmptyStates>
    );
  }

  return <StudentGroupsWrapper>{groups && <GroupsGrid groups={groups} />}</StudentGroupsWrapper>;
};

const GroupsPageFromUserType: FC<GroupsPageFromUserTypeProps> = ({
  isStudent,
  isLoading,
  groups,
  activeTab,
  setActiveTab,
}) => {
  if (isStudent) {
    return (
      <>
        <MainHeadingText style={{ marginBottom: 30 }}>Мои курсы</MainHeadingText>
        <Tabs tabs={groupTabs} activeTab={activeTab} handleClick={setActiveTab} />
        <GroupsPageContainer isLoading={isLoading} groups={groups} activeTab={activeTab} />
      </>
    );
  }

  if (!isStudent) {
    return (
      <>
        <MainHeadingText style={{ marginBottom: 30 }}>Курсы</MainHeadingText>
        <GroupsPageContainer isLoading={isLoading} groups={groups} activeTab={activeTab} />
      </>
    );
  }

  return <MainHeadingText style={{ marginBottom: 30 }}>ЗАГРУЗКА</MainHeadingText>;
};

export const GroupsSection: FC = () => {
  const { role, token } = useAuth();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [groups, setGroups] = useState<GroupCardProps[] | undefined>(undefined);

  // Пропускаем запросы к группам если пользователь не авторизован
  const isAuthorized = !!token && (role === Role.Student || role === Role.Teacher || role === Role.Mentor);
  
  const { data: groupActive, isLoading: groupActiveIsLoading, error: groupActiveError } = groupApi.useGetActiveGroupQuery(undefined, { skip: !isAuthorized });
  const { data: groupArchived, isLoading: groupArchivedIsLoading, error: groupArchivedError } = groupApi.useGetArchivedGroupQuery(undefined, { skip: !isAuthorized });
  // Для студента используем только доступные ему мероприятия; общий список админский может вернуть 403
  // студенту не нужен общий список /events, чтобы не ловить 403; прогресс считаем по userEvents
  const { data: events, isLoading: eventsIsLoading } = eventApi.useGetEventsQuery(undefined, { skip: true });
  const { data: userEvents, isLoading: userEventsIsLoading } = eventApi.useGetUserEventsQuery(undefined, { skip: role !== Role.Student });
  const isLoading = groupActiveIsLoading || groupArchivedIsLoading || userEventsIsLoading;

  const isStudent = role === Role.Student;

  useEffect(() => {
    if (!isLoading) {
      const groupData = activeTab === 0 ? groupActive : groupArchived;
      setGroups(
        groupData?.map(group => {
          const groupStartTime = getDayjsInGMT(group.startTime);
          const groupEndTime = getDayjsInGMT(group.endTime);
          // если есть userEvents — вычислим количество по ним; иначе безопасно 0
          const filteredEventsByGroups = (userEvents||[])
            .map(ue => ue.eventId)
            .filter(Boolean);
          return {
            id: group.id,
            name: group.name,
            info: getInfo({
              date: {
                start: groupStartTime,
                end: groupEndTime,
              },
              eventsCount: filteredEventsByGroups?.length,
            }),
            point: isStudent
              ? {
                  name: 'Прогресс выполнения заданий',
                  max: getGroupMaxPoints(events, group.id),
                  now: getGroupUserPoints(userEvents, events, group.id),
                }
              : undefined,
          };
        }),
      );
    }
  }, [isLoading, activeTab, groupActive, groupArchived, events, userEvents]);

  // Если пользователь не авторизован, показываем сообщение
  if (!isAuthorized) {
    return (
      <SecondaryHeadingTextForEmptyStates>
        Для просмотра групп необходимо войти в систему
      </SecondaryHeadingTextForEmptyStates>
    );
  }

  // Если есть ошибки авторизации, показываем сообщение
  if (groupActiveError || groupArchivedError) {
    return (
      <SecondaryHeadingTextForEmptyStates>
        Ошибка доступа к группам. Проверьте авторизацию.
      </SecondaryHeadingTextForEmptyStates>
    );
  }

  return (
    <GroupsPageFromUserType
      isStudent={isStudent}
      isLoading={isLoading}
      groups={groups}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};
